import { memo, useRef, useState, useEffect, useMemo, type ReactNode } from "react"
import type { GrupoCatalogo } from "@/api"
import { Button, Divider, Stack, Typography, Box, IconButton, Fade } from "@mui/material"
import { alpha, darken, useTheme, type Theme } from "@mui/material/styles"
import { capitalize } from "@/lib/formato"
import { fundoNeutroKiosk } from "./cores-grupo"
import { iconeGrupo, ICONE_TODOS, ICONE_DESTAQUE } from "./icones-grupo"
import IconeCategoria from "./icone-categoria"
import type { CategoriaSelecionada } from "./grade-produtos"
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowRightOutlinedIcon from '@mui/icons-material/KeyboardArrowRightOutlined'

interface CategoriasLateralProps {
    grupos: GrupoCatalogo[]
    selectedCategoryId: CategoriaSelecionada
    onCategorySelect: (id: CategoriaSelecionada) => void
    largura?: string
    alturaCategoria?: string
    tamanhoIcone?: number
    fonteCategoria?: string
    indicadorRolagem?: 'botao' | 'esmaecer'
    alturaAtalho?: string
    rodape?: ReactNode
}

function estiloCategoria(theme: Theme, selecionada: boolean) {
    const fundo = selecionada ? alpha(theme.palette.primary.main, 0.85) : fundoNeutroKiosk(theme)

    return {
        backgroundColor: fundo,
        color: selecionada ? theme.palette.primary.contrastText : theme.palette.text.primary,
        boxShadow: 'none',
        '&:hover': { backgroundColor: darken(fundo, selecionada ? 0.1 : 0.04), boxShadow: 'none' },
    }
}

const corDoIcone = (selecionada: boolean): string => (selecionada ? 'primary.contrastText' : 'text.primary')

function CategoriasLateral({
    grupos,
    selectedCategoryId,
    onCategorySelect,
    largura = '30%',
    alturaCategoria = '125px',
    tamanhoIcone = 50,
    fonteCategoria,
    indicadorRolagem = 'botao',
    alturaAtalho = '125px',
    rodape,
}: CategoriasLateralProps): JSX.Element {
    const theme = useTheme()
    const scrollContainerRef = useRef<HTMLDivElement | null>(null)
    const [canScrollDown, setCanScrollDown] = useState(false)
    const temDestaques = useMemo(() => grupos.some((g) => g.produtos.some((p) => p.destaqueCardapio)), [grupos])

    const checkForScroll = (): void => {
        const el = scrollContainerRef.current
        if (el) setCanScrollDown(el.scrollTop + el.clientHeight < el.scrollHeight - 2)
    }

    const handleScrollDown = (): void => {
        scrollContainerRef.current?.scrollBy({ top: 200, behavior: 'smooth' })
    }

    useEffect(() => {
        checkForScroll()
        window.addEventListener('resize', checkForScroll)
        return () => window.removeEventListener('resize', checkForScroll)
    }, [grupos])

    return (
        <Stack sx={{ p: 2, flexShrink: 0, width: largura, height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
            <Box sx={{ flexShrink: 0, mb: 1 }}>
                <Button
                    fullWidth
                    variant="contained"
                    disableElevation
                    onClick={() => onCategorySelect(null)}
                    sx={{
                        py: 1.5,
                        px: 4,
                        borderRadius: 2,
                        justifyContent: 'center',
                        textTransform: 'none',
                        minHeight: alturaAtalho,
                        position: 'relative',
                        ...estiloCategoria(theme, selectedCategoryId === null),
                    }}
                >
                    <Stack alignItems="center" spacing={0.5} sx={{ width: '100%' }}>
                        <IconeCategoria tamanho={tamanhoIcone} src={ICONE_TODOS} cor={corDoIcone(selectedCategoryId === null)} />

                        <Typography
                            variant="h5"
                            sx={{ width: '100%', textAlign: 'center', fontSize: fonteCategoria, fontWeight: selectedCategoryId === null ? 'bold' : 'normal', wordBreak: 'break-word', lineHeight: 1.2 }}
                        >
                            Todos
                        </Typography>
                    </Stack>

                    {selectedCategoryId === null && (
                        <KeyboardArrowRightOutlinedIcon fontSize="large" sx={{ position: 'absolute', right: 4 }} />
                    )}
                </Button>

                {temDestaques && (
                    <Button
                        fullWidth
                        variant={'contained'}
                        disableElevation
                        onClick={() => onCategorySelect('DESTAQUE')}
                        sx={{
                            mt: 1,
                            py: 1.5,
                            px: 2,
                            borderRadius: 2,
                            minHeight: alturaAtalho,
                            position: 'relative',
                            textTransform: 'none',
                            ...estiloCategoria(theme, selectedCategoryId === 'DESTAQUE'),
                        }}
                    >
                        <Stack alignItems="center" spacing={0.5} sx={{ width: '100%' }}>
                            <IconeCategoria tamanho={tamanhoIcone} src={ICONE_DESTAQUE} cor={corDoIcone(selectedCategoryId === 'DESTAQUE')} />

                            <Typography
                                variant="h5"
                                sx={{ width: '100%', textAlign: 'center', fontSize: fonteCategoria, fontWeight: selectedCategoryId === 'DESTAQUE' ? 'bold' : 'normal', wordBreak: 'break-word', lineHeight: 1.2 }}
                            >
                                Destaques
                            </Typography>
                        </Stack>

                        {selectedCategoryId === 'DESTAQUE' && (
                            <KeyboardArrowRightOutlinedIcon fontSize="large" sx={{ position: 'absolute', right: 4 }} />
                        )}
                    </Button>
                )}

                <Divider sx={{ mt: 1.5, mb: 0.5 }}>
                    <Typography variant="h6">Categorias</Typography>
                </Divider>
            </Box>

            <Stack
                ref={scrollContainerRef}
                onScroll={checkForScroll}
                spacing={1}
                sx={{
                    overflowY: 'auto',
                    overscrollBehavior: 'contain',
                    flexGrow: 1,
                    pr: 0.5,
                    msOverflowStyle: 'none',
                    scrollbarWidth: 'none',
                    '&::-webkit-scrollbar': { display: 'none' },
                }}
            >
                {grupos.map((grupo) => {
                    const isSelected = selectedCategoryId === grupo.id
                    const icone = iconeGrupo(grupo.tipo)

                    return (
                        <Button
                            key={grupo.id}
                            fullWidth
                            disableElevation
                            variant="contained"
                            color="primary"
                            onClick={() => onCategorySelect(grupo.id)}
                            sx={{
                                py: 1,
                                px: 1,
                                minHeight: alturaCategoria,
                                height: 'auto',
                                flexShrink: 0,
                                borderRadius: 2,
                                position: 'relative',
                                whiteSpace: 'normal',
                                textTransform: 'none',
                                ...estiloCategoria(theme, isSelected),
                            }}
                        >
                            <Stack alignItems="center" spacing={0.5} sx={{ width: '100%' }}>
                                {icone && <IconeCategoria tamanho={tamanhoIcone} src={icone} cor={corDoIcone(isSelected)} />}

                                <Typography
                                    variant="h5"
                                    sx={{
                                        width: '100%',
                                        textAlign: 'center',
                                        fontSize: fonteCategoria,
                                        fontWeight: isSelected ? 'bold' : 'normal',
                                        lineHeight: 1.2,
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                        flexWrap: 'nowrap'
                                    }}
                                >
                                    {capitalize(grupo.descricao)}
                                </Typography>
                            </Stack>

                            {isSelected && (
                                <KeyboardArrowRightOutlinedIcon fontSize="large" sx={{ position: 'absolute', right: 4 }} />
                            )}
                        </Button>
                    )
                })}

                <Fade in={canScrollDown && indicadorRolagem === 'botao'} timeout={300} unmountOnExit>
                    <Box
                        onClick={handleScrollDown}
                        sx={{
                            pt: 1,
                            pb: 1,
                            left: theme.spacing(2),
                            right: theme.spacing(2.5),
                            bottom: theme.spacing(2),
                            zIndex: 10,
                            borderRadius: 2,
                            display: 'flex',
                            userSelect: 'none',
                            position: 'absolute',
                            justifyContent: 'center',
                            backgroundColor: 'background.paper',
                            boxShadow: '0px -4px 10px 4px rgba(0,0,0,0.05)',
                            cursor: 'pointer',
                            '&:hover': { backgroundColor: darken(theme.palette.background.paper, 0.05) },
                        }}
                    >
                        <IconButton size="large" sx={{ pointerEvents: 'none' }}>
                            <KeyboardArrowDownIcon fontSize="large" />
                        </IconButton>
                    </Box>
                </Fade>
            </Stack>

            {indicadorRolagem === 'esmaecer' && (
                <Box
                    aria-hidden
                    sx={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        bottom: 0,
                        height: 56,
                        pointerEvents: 'none',
                        opacity: canScrollDown ? 1 : 0,
                        transition: 'opacity .25s ease',
                        background: `linear-gradient(to top, ${theme.palette.background.default} 0%, ${alpha(
                            theme.palette.background.default,
                            0,
                        )} 100%)`,
                    }}
                />
            )}

            {/* Por cima da lista, no canto: sem linha própria, para não tirar altura das categorias. */}
            {rodape && (
                <Box
                    sx={{
                        position: 'absolute',
                        left: 'calc(8px + env(safe-area-inset-left))',
                        bottom: 'calc(8px + env(safe-area-inset-bottom))',
                        zIndex: 11,
                    }}
                >
                    {rodape}
                </Box>
            )}
        </Stack>
    )
}

export default memo(CategoriasLateral)
